import React, { useState, useEffect } from 'react';
import { Button, Badge, Card, Row, Col, Table, Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { actions as aprobacionActions, selectors as aprobacionSelectors } from '../../reducers/aprobaciones';
import VotarAprobacionModal from './VotarAprobacionModal';
import SolicitarAprobacionModal from './SolicitarAprobacionModal';
import { getModuloLabel, getSnapshotResumen } from './moduloLabels';
import moment from 'moment';

const AprobacionesList = ({ proyectoId, projectDetail, usuario, isAdmin }) => {
    const dispatch = useDispatch();
    const solicitudes = useSelector(aprobacionSelectors.getSolicitudes);
    const isLoading = useSelector(aprobacionSelectors.getIsLoading);

    const [showVotarModal, setShowVotarModal] = useState(false);
    const [solicitudParaVotar, setSolicitudParaVotar] = useState(null);
    const [tipoVotoSeleccionado, setTipoVotoSeleccionado] = useState('APROBADO');
    const [showNuevaSolicitudModal, setShowNuevaSolicitudModal] = useState(false);
    const [expandedIds, setExpandedIds] = useState({});

    useEffect(() => {
        if (proyectoId) {
            dispatch(aprobacionActions.getSolicitudes(proyectoId));
        }
    }, [proyectoId, dispatch]);

    const toggleExpand = (id) => {
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleAbrirVotar = (solicitud, tipoVoto) => {
        setSolicitudParaVotar(solicitud);
        setTipoVotoSeleccionado(tipoVoto);
        setShowVotarModal(true);
    };

    const getBadgeEstado = (estado) => {
        switch (estado) {
            case 'APROBADO_TOTAL':
                return <Badge variant="success" className="p-2"><i className="bi bi-check-all mr-1"></i>Aprobado Total</Badge>;
            case 'RECHAZADO_PARCIAL':
                return <Badge variant="danger" className="p-2"><i className="bi bi-x-circle mr-1"></i>Rechazado Parcial</Badge>;
            case 'RECHAZADO_TOTAL':
                return <Badge variant="dark" className="p-2"><i className="bi bi-x-octagon-fill mr-1"></i>Rechazado Total</Badge>;
            case 'EN_PROCESO':
            default:
                return <Badge variant="warning" className="p-2"><i className="bi bi-hourglass-split mr-1"></i>En Proceso</Badge>;
        }
    };

    const getVotoFirmanteBadge = (voto) => {
        switch (voto) {
            case 'APROBADO':
                return <Badge variant="success"><i className="bi bi-check-lg mr-1"></i>Aprobado</Badge>;
            case 'RECHAZADO':
                return <Badge variant="danger"><i className="bi bi-x-lg mr-1"></i>Rechazado</Badge>;
            case 'PENDIENTE':
            default:
                return <Badge variant="secondary"><i className="bi bi-clock mr-1"></i>Pendiente</Badge>;
        }
    };

    const getBorderColor = (estado) => {
        switch (estado) {
            case 'APROBADO_TOTAL': return '#28a745';
            case 'RECHAZADO_PARCIAL':
            case 'RECHAZADO_TOTAL': return '#dc3545';
            case 'EN_PROCESO': return '#ffc107';
            default: return '#dee2e6';
        }
    };

    const stats = {
        total: (solicitudes || []).length,
        enProceso: (solicitudes || []).filter(function (s) { return s.estado === 'EN_PROCESO'; }).length,
        aprobados: (solicitudes || []).filter(function (s) { return s.estado === 'APROBADO_TOTAL'; }).length,
        rechazados: (solicitudes || []).filter(function (s) { return s.estado && s.estado.indexOf('RECHAZADO') === 0; }).length,
    };

    return (
        <div className="aprobaciones-container mt-3">
            {/* Header con botón */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <div>
                    <h4 className="mb-1 text-primary font-weight-bold">
                        <i className="bi bi-patch-check-fill mr-2"></i>
                        Flujo de Aprobaciones - {(projectDetail && projectDetail.nombre) || ''}
                    </h4>
                    <p className="text-muted small mb-0">
                        Historial de solicitudes de aprobación multi-firmante, estados de vistos buenos y firmas.
                    </p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => setShowNuevaSolicitudModal(true)}
                    className="d-flex align-items-center shadow-sm mt-2"
                >
                    <i className="bi bi-plus-circle mr-2"></i>Nueva Solicitud de Aprobación
                </Button>
            </div>

            {/* Métricas */}
            <Row className="mb-4">
                <Col md={3} sm={6} className="mb-3">
                    <Card className="border-0 shadow-sm text-center p-3">
                        <small className="text-muted text-uppercase font-weight-bold">Total Solicitudes</small>
                        <h3 className="mb-0 font-weight-bold text-dark mt-1">{stats.total}</h3>
                    </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <Card className="border-0 shadow-sm text-center p-3" style={{ borderLeft: '4px solid #ffc107' }}>
                        <small className="text-warning text-uppercase font-weight-bold">En Proceso</small>
                        <h3 className="mb-0 font-weight-bold text-dark mt-1">{stats.enProceso}</h3>
                    </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <Card className="border-0 shadow-sm text-center p-3" style={{ borderLeft: '4px solid #28a745' }}>
                        <small className="text-success text-uppercase font-weight-bold">Aprobados Total</small>
                        <h3 className="mb-0 font-weight-bold text-dark mt-1">{stats.aprobados}</h3>
                    </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <Card className="border-0 shadow-sm text-center p-3" style={{ borderLeft: '4px solid #dc3545' }}>
                        <small className="text-danger text-uppercase font-weight-bold">Rechazados</small>
                        <h3 className="mb-0 font-weight-bold text-dark mt-1">{stats.rechazados}</h3>
                    </Card>
                </Col>
            </Row>

            {/* Loading */}
            {isLoading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">Cargando flujo de aprobaciones...</p>
                </div>
            )}

            {/* Empty state */}
            {!isLoading && (!solicitudes || solicitudes.length === 0) && (
                <Card className="text-center py-5 border-0 shadow-sm">
                    <Card.Body>
                        <i className="bi bi-inbox" style={{ fontSize: '2.5rem', color: '#adb5bd', display: 'block', marginBottom: '1rem' }}></i>
                        <h5 className="text-muted">No hay solicitudes de aprobación registradas</h5>
                        <p className="text-muted small">
                            Puede iniciar un flujo de aprobación con el botón "Nueva Solicitud de Aprobación".
                        </p>
                    </Card.Body>
                </Card>
            )}

            {/* Lista de solicitudes */}
            {!isLoading && solicitudes && solicitudes.length > 0 && (
                <div>
                    {solicitudes.map(function (sol, index) {
                        const esFirmante = (sol.Firmantes || []).some(function (f) { return f.usuario_id === (usuario && usuario.id); });
                        const miFirma = (sol.Firmantes || []).find(function (f) { return f.usuario_id === (usuario && usuario.id); });
                        const puedoVotar = sol.estado === 'EN_PROCESO' && esFirmante && miFirma && miFirma.estado_voto === 'PENDIENTE';
                        const totalFirmantes = (sol.Firmantes || []).length;
                        const aprobadosCount = (sol.Firmantes || []).filter(function (f) { return f.estado_voto === 'APROBADO'; }).length;
                        const rechazadosCount = (sol.Firmantes || []).filter(function (f) { return f.estado_voto === 'RECHAZADO'; }).length;
                        const isExpanded = !!expandedIds[sol.id];
                        const borderColor = getBorderColor(sol.estado);

                        const solicitantePersona = sol.Solicitante && sol.Solicitante.Persona;
                        const solicitanteNombre = solicitantePersona
                            ? (solicitantePersona.nombre + ' ' + solicitantePersona.apellido)
                            : (sol.Solicitante && sol.Solicitante.username) || '—';

                        return (
                            <Card
                                key={sol.id}
                                className="mb-3 shadow-sm"
                                style={{ borderLeft: '4px solid ' + borderColor }}
                            >
                                {/* Header clickeable */}
                                <Card.Header
                                    className="bg-white d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => toggleExpand(sol.id)}
                                >
                                    <div className="d-flex align-items-center">
                                        <span className="badge badge-light border mr-2">#{sol.id}</span>
                                        <div>
                                            <strong>{sol.titulo}</strong>
                                            <small className="text-muted d-block">
                                                Módulo: <span className="text-primary font-weight-bold">{getModuloLabel(sol.modulo_tipo)}</span>
                                                {sol.referencia_id ? (
                                                    <span>{' • '}Referencia: #{sol.referencia_id}</span>
                                                ) : null}
                                                {' • '}
                                                {moment(sol.createdAt || sol.created_at).format('DD/MM/YYYY HH:mm')}
                                                {' • por '}{solicitanteNombre}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span className="mr-3 d-none d-md-block text-right">
                                            <small className="text-muted d-block">Firmas</small>
                                            <span className="font-weight-bold">{aprobadosCount}/{totalFirmantes} Aprobados</span>
                                            {rechazadosCount > 0 && <span className="text-danger ml-1">({rechazadosCount} Rechazo)</span>}
                                        </span>
                                        {getBadgeEstado(sol.estado)}
                                        <i className={'bi ml-3 ' + (isExpanded ? 'bi-chevron-up' : 'bi-chevron-down')}></i>
                                    </div>
                                </Card.Header>

                                {/* Body expandible */}
                                {isExpanded && (
                                    <Card.Body className="bg-light">
                                        {(sol.referencia_id || getSnapshotResumen(sol.snapshot_data)) && (
                                            <div className="mb-3 p-3 bg-white border rounded d-flex align-items-start">
                                                <i className="bi bi-bullseye fs-4 me-3 text-primary mt-1"></i>
                                                <div className="w-100">
                                                    <small className="text-muted font-weight-bold d-block text-uppercase">Elemento a aprobar</small>
                                                    <div className="mt-1">
                                                        <Badge variant="primary" className="mr-2">{getModuloLabel(sol.modulo_tipo)}</Badge>
                                                        {sol.referencia_id && (
                                                            <Badge variant="secondary">Referencia: #{sol.referencia_id}</Badge>
                                                        )}
                                                    </div>
                                                    {getSnapshotResumen(sol.snapshot_data) && (
                                                        <small className="text-muted d-block mt-1">
                                                            <strong>Documento:</strong> {getSnapshotResumen(sol.snapshot_data)}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {sol.descripcion && (
                                            <div className="mb-3 p-3 bg-white border rounded">
                                                <small className="text-muted font-weight-bold d-block text-uppercase">Instrucciones / Descripción:</small>
                                                <p className="mb-0 mt-1">{sol.descripcion}</p>
                                            </div>
                                        )}

                                        {/* Panel de votación */}
                                        {puedoVotar && (
                                            <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap shadow-sm">
                                                <div>
                                                    <i className="bi bi-exclamation-circle-fill mr-2" style={{ fontSize: '1.1rem' }}></i>
                                                    <strong>Acción Requerida:</strong> Usted está asignado como firmante y su voto está pendiente.
                                                </div>
                                                <div className="d-flex mt-2">
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        className="d-flex align-items-center mr-2"
                                                        onClick={() => handleAbrirVotar(sol, 'APROBADO')}
                                                    >
                                                        <i className="bi bi-check2-circle mr-1"></i>Dar Visto Bueno
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => handleAbrirVotar(sol, 'RECHAZADO')}
                                                    >
                                                        <i className="bi bi-x-circle mr-1"></i>Rechazar
                                                    </Button>
                                                </div>
                                            </Alert>
                                        )}

                                        <h6 className="font-weight-bold text-secondary mb-3">
                                            <i className="bi bi-people-fill mr-2"></i>Firmantes y Estado de Aprobación
                                        </h6>
                                        <div className="table-responsive bg-white rounded border shadow-sm">
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th>Firmante</th>
                                                        <th>Email</th>
                                                        <th>Estado de la Firma</th>
                                                        <th>Fecha de Voto</th>
                                                        <th>Comentarios</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(sol.Firmantes || []).map(function (f) {
                                                        const persona = f.Usuario && f.Usuario.Persona;
                                                        const nombre = persona
                                                            ? (persona.nombre + ' ' + persona.apellido)
                                                            : ((f.Usuario && f.Usuario.username) || 'Usuario');
                                                        return (
                                                            <tr key={f.id}>
                                                                <td><strong>{nombre}</strong></td>
                                                                <td><small className="text-muted">{f.Usuario && f.Usuario.username}</small></td>
                                                                <td>{getVotoFirmanteBadge(f.estado_voto)}</td>
                                                                <td>
                                                                    {f.fecha_voto ? (
                                                                        <small>
                                                                            <i className="bi bi-calendar-event mr-1"></i>
                                                                            {moment(f.fecha_voto).format('DD/MM/YYYY HH:mm')}
                                                                        </small>
                                                                    ) : (
                                                                        <span className="text-muted small">—</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {f.comentario ? (
                                                                        <span className="small">{f.comentario}</span>
                                                                    ) : (
                                                                        <span className="text-muted small">Sin comentarios</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Modales */}
            {solicitudParaVotar && (
                <VotarAprobacionModal
                    show={showVotarModal}
                    onHide={() => setShowVotarModal(false)}
                    solicitud={solicitudParaVotar}
                    proyectoId={proyectoId}
                    tipoVoto={tipoVotoSeleccionado}
                />
            )}

            <SolicitarAprobacionModal
                show={showNuevaSolicitudModal}
                onHide={() => setShowNuevaSolicitudModal(false)}
                proyectoId={proyectoId}
                moduloTipo="GENERAL"
                tituloDefault={'Aprobación General - ' + ((projectDetail && projectDetail.nombre) || '')}
            />
        </div>
    );
};

export default AprobacionesList;
